/**
 * Square Payment Form Component
 * 
 * Integrates Square Web Payments SDK for secure card entry.
 * Uses a portal-like approach to prevent React/Square DOM conflicts.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  publicCatchMessage,
  publicGuardUserMessage,
  publicMessageFromApiErrorResponse,
} from '../lib/publicSiteErrorMessage';

// Square Web Payments SDK types
declare global {
  interface Window {
    Square?: {
      payments: (appId: string, locationId: string) => Promise<Payments>;
    };
  }
}

interface Payments {
  card: () => Promise<Card>;
}

interface Card {
  attach: (selector: string) => Promise<void>;
  tokenize: () => Promise<TokenResult>;
  destroy: () => Promise<void>;
}

interface TokenResult {
  status: string;
  token?: string;
  errors?: Array<{ message: string }>;
}

interface SquarePaymentFormProps {
  amount: number;
  onPaymentSuccess: (paymentId: string, receiptUrl?: string) => void;
  onPaymentError: (error: string) => void;
  onCancel?: () => void;
  customerEmail?: string;
  customerName?: string;
  orderId?: string;
  /** Optional fundraiser campaign (echoed to POST /api/payments for Square note + server checks). */
  campaignId?: string;
  campaignSlug?: string;
  disabled?: boolean;
}

// API URL - use relative path in production when VITE_API_URL is not set
const API_URL = import.meta.env.VITE_API_URL || '';

// Unique ID for card container to avoid conflicts
const CARD_CONTAINER_ID = 'sq-card-container';

export default function SquarePaymentForm({
  amount,
  onPaymentSuccess,
  onPaymentError,
  onCancel,
  customerEmail,
  customerName,
  orderId,
  campaignId,
  campaignSlug,
  disabled = false,
}: SquarePaymentFormProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [isCardReady, setIsCardReady] = useState(false);
  const [squareConfig, setSquareConfig] = useState<{
    applicationId: string;
    locationId: string;
    environment: string;
  } | null>(null);
  
  const cardRef = useRef<Card | null>(null);
  const mountedRef = useRef(true);
  const initAttemptedRef = useRef(false);
  /** Synchronous guard: React state updates batch, so two fast Pay clicks can both pass `isProcessing` checks. */
  const paySubmitLockRef = useRef(false);
  // Stable callbacks — parent often passes inline handlers; putting them in effect deps remounts
  // the Square card iframe mid-checkout and breaks Pay.
  const onPaymentErrorRef = useRef(onPaymentError);
  const onPaymentSuccessRef = useRef(onPaymentSuccess);
  onPaymentErrorRef.current = onPaymentError;
  onPaymentSuccessRef.current = onPaymentSuccess;

  // Fetch Square config from backend
  useEffect(() => {
    async function fetchConfig() {
      try {
        const response = await fetch(`${API_URL}/api/square/config`);
        let data: Record<string, unknown> = {};
        try {
          data = (await response.json()) as Record<string, unknown>;
        } catch (parseErr) {
          console.error('Square config: response was not JSON', parseErr);
          onPaymentErrorRef.current(publicGuardUserMessage(''));
          setIsLoading(false);
          return;
        }
        if (!response.ok) {
          onPaymentErrorRef.current(publicMessageFromApiErrorResponse(response.status, data));
          setIsLoading(false);
          return;
        }
        if (data.configured && data.applicationId && data.locationId) {
          setSquareConfig({
            applicationId: data.applicationId as string,
            locationId: data.locationId as string,
            environment: (data.environment as string) || 'sandbox',
          });
        } else {
          const raw =
            typeof data.error === 'string' && data.error.trim()
              ? data.error
              : 'Payment system not configured';
          onPaymentErrorRef.current(publicGuardUserMessage(raw));
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to fetch Square config:', error);
        onPaymentErrorRef.current(publicCatchMessage(error));
        setIsLoading(false);
      }
    }
    
    fetchConfig();
  }, []);

  // Initialize the card payment form
  const initializeCard = useCallback(async () => {
    if (!squareConfig || !window.Square || initAttemptedRef.current) return;
    
    initAttemptedRef.current = true;

    try {
      // Create the container if it doesn't exist
      let container = document.getElementById(CARD_CONTAINER_ID);
      if (!container) {
        console.error('Card container not found');
        setIsLoading(false);
        return;
      }

      // Clear any existing content
      container.innerHTML = '';

      const payments = await window.Square.payments(
        squareConfig.applicationId,
        squareConfig.locationId
      );

      const card = await payments.card();

      // Attach to the container
      await card.attach(`#${CARD_CONTAINER_ID}`);
      
      if (mountedRef.current) {
        cardRef.current = card;
        setIsCardReady(true);
        setIsLoading(false);
      } else {
        // Component unmounted during init, clean up
        card.destroy().catch(() => {});
      }
    } catch (error) {
      console.error('Failed to initialize card:', error);
      const err = error as Error & { errors?: Array<{ detail?: string }> };
      const detail = err?.message || err?.errors?.[0]?.detail;
      if (mountedRef.current) {
        // Surface Square's actual error so we can fix config (e.g. wrong env, location not enabled)
        const message = detail
          ? `Payment form could not load: ${detail}`
          : 'Failed to initialize payment form. Check Integrations and try again.';
        onPaymentErrorRef.current(publicGuardUserMessage(message));
        setIsLoading(false);
      }
    }
  }, [squareConfig]);

  // Load Square SDK and initialize
  useEffect(() => {
    if (!squareConfig) return;

    mountedRef.current = true;
    initAttemptedRef.current = false;

    const loadAndInit = async () => {
      const existingScript = document.querySelector('script[src*="square.js"]');
      // Check if SDK is already loaded
      if (window.Square) {
        initializeCard();
        return;
      }

      // Check if script is already being loaded
      if (existingScript) {
        // Wait for it to load
        existingScript.addEventListener('load', () => initializeCard());
        return;
      }

      // Load the SDK script (use production or sandbox based on environment)
      const script = document.createElement('script');
      const sdkUrl = squareConfig.environment === 'production'
        ? 'https://web.squarecdn.com/v1/square.js'
        : 'https://sandbox.web.squarecdn.com/v1/square.js';
      script.src = sdkUrl;
      script.async = true;
      script.onload = () => {
        if (mountedRef.current) {
          initializeCard();
        }
      };
      script.onerror = () => {
        if (mountedRef.current) {
          onPaymentErrorRef.current(publicGuardUserMessage('Failed to load payment SDK'));
          setIsLoading(false);
        }
      };
      
      document.body.appendChild(script);
    };

    loadAndInit();

    // Cleanup function
    return () => {
      mountedRef.current = false;
      
      // Destroy card instance
      if (cardRef.current) {
        const card = cardRef.current;
        cardRef.current = null;
        
        // Async destroy - don't await, just fire and forget
        card.destroy().catch(() => {});
      }
      
      // Clear the container manually to avoid React conflicts
      const container = document.getElementById(CARD_CONTAINER_ID);
      if (container) {
        // Use setTimeout to let Square cleanup first
        setTimeout(() => {
          if (container && container.parentNode) {
            container.innerHTML = '';
          }
        }, 100);
      }
    };
  }, [squareConfig, initializeCard]);

  // Handle payment submission
  const handlePayment = async () => {
    if (!cardRef.current || disabled || !isCardReady) return;
    if (paySubmitLockRef.current) return;
    paySubmitLockRef.current = true;
    setIsProcessing(true);
    setCardError(null);

    let paymentSucceeded = false;
    try {
      // Tokenize the card
      const result = await cardRef.current.tokenize();

      if (result.status !== 'OK' || !result.token) {
        const errorMessage = result.errors?.[0]?.message || 'Card verification failed';
        setCardError(errorMessage);
        paySubmitLockRef.current = false;
        setIsProcessing(false);
        return;
      }

      // Send token to backend to process payment
      const response = await fetch(`${API_URL}/api/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sourceId: result.token,
          amount,
          orderId,
          customerEmail,
          customerName,
          note: `Website order${orderId ? ` #${orderId}` : ''}`,
          ...(campaignId ? { campaignId } : {}),
          ...(campaignSlug ? { campaignSlug } : {}),
        }),
      });

      let data: Record<string, unknown> = {};
      try {
        data = (await response.json()) as Record<string, unknown>;
      } catch {
        data = {};
      }

      if (!response.ok) {
        throw new Error(publicMessageFromApiErrorResponse(response.status, data));
      }

      paymentSucceeded = true;
      onPaymentSuccessRef.current(data.paymentId as string, data.receiptUrl as string | undefined);
    } catch (error) {
      console.error('Payment error:', error);
      const message = publicCatchMessage(error);
      setCardError(message);
      onPaymentErrorRef.current(message);
    } finally {
      setIsProcessing(false);
      if (!paymentSucceeded) paySubmitLockRef.current = false;
    }
  };

  return (
    <div className={`square-payment-form ${isProcessing ? 'pointer-events-none opacity-90' : ''}`}>
      {/* Card Input Container - Square SDK will inject iframe here */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Details
        </label>
        <div
          className={`
            border rounded-lg p-3 bg-white min-h-[50px]
            ${isLoading ? 'animate-pulse bg-gray-100' : ''}
            ${cardError ? 'border-red-500' : 'border-gray-300'}
          `}
        >
          {/* This div is managed by Square SDK - React won't touch its children */}
          {/* Using CSS custom property for dynamic minHeight (required for Square SDK container) */}
          <div 
            id={CARD_CONTAINER_ID}
            className={isLoading ? 'square-card-loading' : 'square-card-ready'}
          />
          {isLoading && (
            <div className="flex items-center justify-center h-[50px] text-gray-400">
              Loading secure payment form...
            </div>
          )}
        </div>
        
        {/* Error Message */}
        {cardError && (
          <p className="mt-2 text-sm text-red-600">{cardError}</p>
        )}
      </div>

      {/* Amount Display */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Total to pay:</span>
          <span className="text-2xl font-bold text-primary-600">
            ${amount.toFixed(2)} CAD
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Back
          </button>
        )}
        <button
          type="button"
          onClick={handlePayment}
          disabled={isLoading || isProcessing || disabled || !isCardReady}
          className={`
            flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-colors
            ${isProcessing ? 'bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'}
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Processing payment…
            </span>
          ) : (
            `Pay $${amount.toFixed(2)}`
          )}
        </button>
      </div>

      {/* Security Notice */}
      <p className="mt-4 text-xs text-gray-500 text-center flex items-center justify-center gap-1">
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
            clipRule="evenodd"
          />
        </svg>
        Secured by Square. Your card details are encrypted.
      </p>
    </div>
  );
}
